/**
 * WorkspaceBrandingPage - Configuration du branding par workspace.
 * 
 * Features:
 * - Toggle watermark "Powered by Totemium"
 * - Upload logo personnalisé
 * - Couleurs primaire et secondaire
 * - Vérification plan pour autoriser personnalisation
 * - Gestion erreurs (403 si plan insuffisant)
 * 
 * @security Vérification feature "custom_branding" dans plan
 */
import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Upload, Palette, Eye, AlertCircle, CheckCircle2 } from 'lucide-react'
import { api } from '../lib/api'
import { WorkspaceBranding } from '../types/workspace'

export default function WorkspaceBrandingPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>()
  const [branding, setBranding] = useState<WorkspaceBranding | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [canRemoveBranding, setCanRemoveBranding] = useState(false)
  const [canCustomBranding, setCanCustomBranding] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)

  const fetchBranding = useCallback(async () => {
    if (!workspaceId) return
    
    setLoading(true)
    try {
      const { data } = await api.get(`/workspaces/${workspaceId}/branding`)
      setBranding(data)
      
      // Vérifier si le plan permet de retirer/personnaliser le branding
      // TODO: Récupérer plan depuis endpoint dédié
      // Pour l'instant, on suppose que les features sont disponibles
      setCanRemoveBranding(true) // Temporaire
      setCanCustomBranding(true) // Temporaire
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors du chargement')
    } finally {
      setLoading(false)
    }
  }, [workspaceId])

  useEffect(() => {
    fetchBranding()
  }, [fetchBranding])

  const handleToggleWatermark = async (enabled: boolean) => {
    if (!workspaceId || !branding) return
    
    setSaving(true)
    setError(null)
    setSuccess(null)
    
    try {
      const { data } = await api.patch(`/workspaces/${workspaceId}/branding`, {
        watermark_enabled: enabled
      })
      setBranding(data)
      setSuccess('Watermark mis à jour avec succès')
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de la mise à jour')
      // Revert
      setBranding(prev => prev ? { ...prev, watermark_enabled: !enabled } : null)
    } finally {
      setSaving(false)
    }
  }

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !workspaceId) return

    // Validation
    if (!file.type.startsWith('image/')) {
      setError('Le fichier doit être une image')
      return
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('Le logo ne doit pas dépasser 2 MB')
      return
    }

    setUploadingLogo(true)
    setError(null)
    setSuccess(null)

    try {
      const formData = new FormData()
      formData.append('logo', file)

      const { data } = await api.post(`/workspaces/${workspaceId}/branding/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      setBranding(data)
      setSuccess('Logo mis à jour avec succès')
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de l\'upload du logo')
    } finally {
      setUploadingLogo(false)
    }
  }

  const handleColorChange = async (field: 'primary_color' | 'secondary_color', color: string) => {
    if (!workspaceId) return

    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const { data } = await api.patch(`/workspaces/${workspaceId}/branding`, {
        [field]: color
      })
      setBranding(data)
      setSuccess('Couleur mise à jour avec succès')
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de la mise à jour')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-zinc-500">Chargement...</div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
          Branding du Workspace
        </h1>
        <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
          Personnalisez l'apparence de votre widget et chat
        </p>
      </div>

      {/* Messages de feedback */}
      {error && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}
      
      {success && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-emerald-600 dark:text-emerald-400">{success}</p>
        </div>
      )}
      
      {/* Watermark */}
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1 flex-1">
            <label 
              htmlFor="watermark" 
              className="text-sm font-medium text-zinc-900 dark:text-zinc-100"
            >
              Watermark "Powered by Totemium"
            </label>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              {canRemoveBranding 
                ? "Afficher ou masquer le watermark dans le widget"
                : "Passez à un plan supérieur pour retirer le watermark"}
            </p>
          </div>
          
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              id="watermark"
              type="checkbox"
              checked={branding?.watermark_enabled ?? true}
              onChange={(e) => handleToggleWatermark(e.target.checked)}
              disabled={!canRemoveBranding || saving}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 dark:peer-focus:ring-emerald-800 rounded-full peer dark:bg-zinc-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-zinc-600 peer-checked:bg-emerald-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
          </label>
        </div>
      </div>

      {/* Logo personnalisé */}
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <Upload className="h-5 w-5 text-zinc-400" />
          </div>
          <div className="flex-1 space-y-4">
            <div>
              <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                Logo personnalisé
              </h3>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                Remplacez le logo Totemium par votre propre logo (max 2 MB)
              </p>
            </div>

            {branding?.logo_url && (
              <div className="flex items-center gap-4 p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800">
                <img 
                  src={branding.logo_url} 
                  alt="Logo actuel" 
                  className="h-12 w-auto object-contain"
                />
                <div className="flex-1 text-sm text-zinc-600 dark:text-zinc-400">
                  Logo actuel
                </div>
              </div>
            )}

            <div>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={!canCustomBranding || uploadingLogo}
                className="block w-full text-sm text-zinc-500 dark:text-zinc-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 dark:file:bg-emerald-900/20 dark:file:text-emerald-400 dark:hover:file:bg-emerald-900/30 file:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              />
              {uploadingLogo && (
                <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
                  Upload en cours...
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Couleurs */}
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <Palette className="h-5 w-5 text-zinc-400" />
          </div>
          <div className="flex-1 space-y-4">
            <div>
              <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                Couleurs personnalisées
              </h3>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                Personnalisez les couleurs de votre widget
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                  Couleur primaire
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={branding?.primary_color || '#10b981'}
                    onChange={(e) => handleColorChange('primary_color', e.target.value)}
                    disabled={!canCustomBranding || saving}
                    className="h-10 w-20 rounded-lg border border-zinc-300 dark:border-zinc-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <input
                    type="text"
                    value={branding?.primary_color || '#10b981'}
                    onChange={(e) => handleColorChange('primary_color', e.target.value)}
                    disabled={!canCustomBranding || saving}
                    placeholder="#10b981"
                    className="flex-1 px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                  Couleur secondaire
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={branding?.secondary_color || '#059669'}
                    onChange={(e) => handleColorChange('secondary_color', e.target.value)}
                    disabled={!canCustomBranding || saving}
                    className="h-10 w-20 rounded-lg border border-zinc-300 dark:border-zinc-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <input
                    type="text"
                    value={branding?.secondary_color || '#059669'}
                    onChange={(e) => handleColorChange('secondary_color', e.target.value)}
                    disabled={!canCustomBranding || saving}
                    placeholder="#059669"
                    className="flex-1 px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                  />
                </div>
              </div>
            </div>

            {/* Aperçu */}
            <div className="mt-4 p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800">
              <div className="flex items-center gap-2 mb-3">
                <Eye className="h-4 w-4 text-zinc-500" />
                <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Aperçu</span>
              </div>
              <div className="flex gap-3">
                <div 
                  className="h-12 w-24 rounded-lg border border-zinc-300 dark:border-zinc-600"
                  style={{ backgroundColor: branding?.primary_color || '#10b981' }}
                />
                <div 
                  className="h-12 w-24 rounded-lg border border-zinc-300 dark:border-zinc-600"
                  style={{ backgroundColor: branding?.secondary_color || '#059669' }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Upgrade prompt */}
      {(!canRemoveBranding || !canCustomBranding) && (
        <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-4">
          <h3 className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">
            🚀 Passez à un plan supérieur
          </h3>
          <p className="mt-1 text-sm text-emerald-700 dark:text-emerald-300">
            Les plans Pro et Enterprise permettent de personnaliser entièrement votre branding.
          </p>
          <a
            href="/pricing"
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors"
          >
            Voir les plans
          </a>
        </div>
      )}
    </div>
  )
}
